import React, { useState } from 'react';
import { 
  Printer, Download, ShieldCheck, CheckCircle2, 
  CreditCard, ArrowRight, Award, QrCode, FileText 
} from 'lucide-react';
import { INSTITUTION_INFO } from '../../data/portalData';
import { PaystackModal } from './PaystackModal';

export const AdmissionLetter = ({ 
  applicant, 
  setActiveView, 
  onAcceptancePaid 
}) => {
  const [showPaystackModal, setShowPaystackModal] = useState(false);
  const [acceptancePaid, setAcceptancePaid] = useState(false);

  // Default applicant fallback
  const candidate = applicant || {
    applicationNo: "AKSU-BS-2026-APP-8841",
    fullName: "Obong Victor Okon",
    programmeApplied: "Doctor of Business Administration (DBA)",
    department: "Business Administration",
    matricNo: "AKSU/BS/2026/DBA/019",
    paystackRef: "PSTK_LIVE_998471203"
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAcceptanceSuccess = (paymentDetails) => {
    setShowPaystackModal(false);
    setAcceptancePaid(true);
    if (onAcceptancePaid) {
      onAcceptancePaid({
        ...candidate,
        acceptancePaid: true,
        acceptanceRef: paymentDetails.reference
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 print:max-w-none print:w-full print:m-0 print:p-0">
      
      {/* Action Strip (Strictly Hidden on Print) */}
      <div className="print:hidden bg-gradient-to-r from-[#030F1C] to-[#0B2644] text-white rounded-2xl p-4 sm:p-5 mb-8 flex flex-wrap items-center justify-between gap-4 shadow-xl border-2 border-[#7A470E]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#7A470E] text-white flex items-center justify-center font-bold">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-white font-sc">Official Letter of Provisional Admission</div>
            <div className="text-xs text-[#B87A28]">Intended Domain: {INSTITUTION_INFO.domain}</div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-[#030F1C] hover:bg-[#06182B] text-white font-bold rounded-lg text-xs transition-colors flex items-center space-x-1.5 cursor-pointer border border-slate-800 font-sc shadow-sm"
          >
            <Printer className="w-4 h-4" />
            <span>Print / Save PDF (A4)</span>
          </button>

          {!acceptancePaid ? (
            <button
              onClick={() => setShowPaystackModal(true)}
              className="px-5 py-2 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black rounded-xl text-xs transition-colors flex items-center space-x-1.5 cursor-pointer shadow-md font-sc"
            >
              <CreditCard className="w-4 h-4" />
              <span>Pay ₦50,000 Acceptance Fee</span>
            </button>
          ) : (
            <button
              onClick={() => setActiveView('enrolment')}
              className="px-5 py-2 bg-[#0B2644] hover:bg-[#06182B] text-white font-black rounded-xl text-xs transition-colors flex items-center space-x-1.5 cursor-pointer shadow-md font-sc"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Proceed to Enrolment</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Official Printed Letterhead Container (Stretched to Fill Exact 1-Page A4 Sheet) */}
      <div className="a4-print-sheet bg-white rounded-2xl border-2 border-slate-300 shadow-xl p-8 sm:p-12 print:border-none print:shadow-none print:rounded-none text-slate-900 relative overflow-hidden font-sans">
        
        {/* Authentic Transparent Watermark of AKSU Crest */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.05] print:opacity-[0.04]">
          <img src="/Logo.png" alt="Watermark" className="w-[500px] h-[500px] print:w-[440px] print:h-[440px] object-contain" />
        </div>

        {/* Top & Main Body Section */}
        <div className="flex-1 flex flex-col justify-between">
          
          {/* Header Strip with Crest & Official Letterhead */}
          <div className="border-b-4 print:border-b-2 border-[#7A470E] pb-5 mb-5 print:pb-3.5 print:mb-3.5">
            <div className="flex items-center justify-between gap-4 print:gap-3">
              <img 
                src="/Logo.png" 
                alt="Akwa Ibom State University Official Crest" 
                className="w-24 h-24 sm:w-28 sm:h-28 print:w-20 print:h-20 object-contain shrink-0 logo-glow select-none" 
              />

              <div className="text-center flex-1">
                <h2 className="text-xs sm:text-sm print:text-xs font-extrabold tracking-widest uppercase text-[#7A470E] font-sc">
                  Akwa Ibom State University
                </h2>
                <h1 className="text-2xl sm:text-3xl print:text-2xl font-black font-sc text-[#0B2644] tracking-wide mt-0.5">
                  BUSINESS SCHOOL
                </h1>
                <p className="text-[12px] print:text-[11px] font-black uppercase tracking-widest text-[#7A470E] mt-0.5 font-sc">
                  Knowledge · Enterprise · Leadership
                </p>
                <p className="text-[11px] print:text-[10px] text-slate-600 mt-1">
                  Main Campus: Ikot Akpaden | Obio Akpa Campus: Oruk Anam | Executive Study Centre: Uyo
                </p>
                <p className="text-[10px] print:text-[9.5px] text-slate-500 font-mono">
                  Email: {INSTITUTION_INFO.contacts.generalEmail} | Portal: {INSTITUTION_INFO.domain}
                </p>
              </div>

              <div className="w-20 h-20 sm:w-24 sm:h-24 print:w-18 print:h-18 rounded-xl border-2 print:border border-[#7A470E]/40 bg-amber-50/50 flex flex-col items-center justify-center p-2 print:p-1 text-center shrink-0">
                <QrCode className="w-12 h-12 print:w-10 print:h-10 text-[#0B2644]" />
                <span className="text-[8px] print:text-[7.5px] font-mono text-[#7A470E] font-bold mt-1">VERIFY ONLINE</span>
              </div>
            </div>
          </div>

          {/* Ref and Date line */}
          <div className="flex justify-between items-center text-xs print:text-[11px] font-mono text-slate-600 mb-5 print:mb-3">
            <div>
              <strong>Ref No:</strong> AKSU/BS/ADM/2026/{candidate.applicationNo.slice(-4)}
            </div>
            <div>
              <strong>Date:</strong> 18th October 2026
            </div>
          </div>

          {/* Addressee */}
          <div className="mb-5 print:mb-3 text-xs print:text-[11px] text-slate-800 space-y-1 print:space-y-1">
            <div className="font-bold text-base print:text-sm text-slate-950 font-sc">{candidate.fullName}</div>
            <div>Application Docket: <span className="font-mono text-slate-800 font-bold">{candidate.applicationNo}</span></div>
            <div>Allocated Matriculation Number: <span className="font-mono font-bold text-[#7A470E]">{candidate.matricNo || 'AKSU/BS/2026/EXEC/019'}</span></div>
            <div>Federal Republic of Nigeria</div>
          </div>

          {/* Formal Subject */}
          <div className="mb-5 print:mb-3 text-center">
            <div className="inline-block border-b-2 border-[#7A470E] pb-1">
              <h3 className="text-base sm:text-lg print:text-[13px] font-black uppercase tracking-wide text-[#0B2644] font-sc">
                Offer of Provisional Admission into Executive Programme
              </h3>
              <span className="text-xs print:text-[11px] font-bold text-[#7A470E] block font-sc mt-0.5">
                2025/2026 & 2026/2027 Executive Academic Session
              </span>
            </div>
          </div>

          {/* Letter Body in British English */}
          <div className="space-y-3.5 print:space-y-3 text-xs sm:text-sm print:text-[11px] text-slate-800 leading-relaxed print:leading-relaxed text-justify">
            <p>
              Dear Scholar,
            </p>
            <p>
              I am pleased to inform you that on the recommendation of the Admissions Screening Committee 
              and with the approval of the University Senate, you have been offered provisional admission into 
              the <strong>Akwa Ibom State University Business School</strong> to pursue an executive course of study leading 
              to the award of:
            </p>

            <div className="my-3.5 print:my-2.5 p-4 print:p-3 rounded-xl bg-amber-50/50 print:bg-slate-50 border-2 print:border border-[#7A470E]/30 text-center">
              <div className="text-lg sm:text-xl print:text-base font-black font-sc text-[#0B2644]">
                {candidate.programmeApplied}
              </div>
              <div className="text-xs print:text-[11px] text-[#7A470E] font-bold mt-1 font-sc">
                Department of {candidate.department} · Faculty of Management Sciences
              </div>
              <div className="text-xs print:text-[10px] font-semibold text-slate-700 mt-0.5">
                Delivery Format: Full-time / Virtual / Hybrid (Executive Modules & Digital LMS)
              </div>
            </div>

            <p>
              This offer is subject to the following statutory academic conditions:
            </p>

            <ol className="list-decimal pl-5 print:pl-5 space-y-2 print:space-y-2 text-xs print:text-[10.5px] text-slate-700 leading-normal">
              <li>
                <strong>Payment of Acceptance Fee:</strong> You are required to confirm this provisional offer within 
                fourteen (14) days by paying the non-refundable acceptance fee of <strong>₦50,000.00 (Fifty Thousand Naira)</strong> via 
                the integrated University Paystack portal.
              </li>
              <li>
                <strong>Original Credentials Verification:</strong> Presentation of certified true copies and original certificates 
                (Degrees, Transcripts, NYSC Discharge/Exemption certificates, and professional fellowships) during the matriculation screening.
              </li>
              <li>
                <strong>Executive Matriculation & Lectures:</strong> Mandatory attendance of the Executive Induction Seminar and 
                Matriculation Ceremony scheduled for <strong>October 2026</strong>.
              </li>
              <li>
                <strong>Code of Executive Conduct:</strong> Compliance with all postgraduate university statutes and the code of academic integrity.
              </li>
            </ol>

            <p>
              On behalf of the Vice-Chancellor, <strong>Prof. Nse Essien</strong>, the University Senate, and the Governing Council, 
              I warmly congratulate you on this milestone and look forward to welcoming you into our community of visionary scholars and enterprise leaders.
            </p>
          </div>

        </div>

        {/* Official Signatures & Seal (Anchored at Base of A4 Page) */}
        <div className="mt-8 print:mt-4 pt-5 print:pt-4 border-t-2 border-slate-300 flex flex-wrap items-end justify-between gap-6 print:gap-3 text-xs print:text-[10.5px]">
          
          <div className="space-y-1 print:space-y-0.5">
            <div className="w-48 h-10 print:h-8 flex items-center">
              <span className="font-sans italic text-base print:text-sm text-[#0B2644] font-extrabold border-b-2 border-[#0B2644] tracking-wide font-sc">
                Dr Uwem Etim Uwah
              </span>
            </div>
            <div className="font-black text-slate-900 font-sc text-sm print:text-xs">
              Dr Uwem Etim Uwah, <span className="font-sans font-bold text-xs print:text-[10px] text-[#7A470E]">PhD, FCA, ACTI</span>
            </div>
            <div className="text-slate-600 font-medium font-sans">Acting Director, AKSU Business School</div>
            <div className="text-slate-400 text-[11px] print:text-[9.5px] font-sans">Akwa Ibom State University</div>
          </div>

          {/* Official Embossed Stamp */}
          <div className="w-28 h-28 print:w-20 print:h-20 rounded-full border-4 print:border-2 border-dashed border-[#7A470E] p-2 print:p-1 flex flex-col items-center justify-center text-center text-[#0B2644] rotate-[-8deg] select-none pointer-events-none">
            <span className="text-[8px] print:text-[6px] font-black uppercase tracking-wider font-sc">AKSU BUSINESS SCHOOL</span>
            <span className="text-[11px] print:text-[8px] font-black font-sc">ADMISSIONS</span>
            <span className="text-[7px] print:text-[5.5px] font-bold uppercase font-sc text-[#7A470E]">APPROVED & REGISTERED</span>
            <span className="text-[8px] print:text-[6.5px] font-mono mt-0.5 print:mt-0">SEPT 2026</span>
          </div>

          <div className="space-y-1 print:space-y-0.5 text-right">
            <div className="w-36 h-10 print:h-8 flex items-center justify-end">
              <span className="font-serif italic text-sm print:text-xs text-[#7A470E] font-bold border-b border-[#7A470E]">
                Dr. (Mrs) E. S. Udofia
              </span>
            </div>
            <div className="font-black text-slate-900 font-sc text-sm print:text-xs">
              Dr. (Mrs) E. S. Udofia
            </div>
            <div className="text-slate-600 font-medium">Registrar & Secretary to Senate</div>
            <div className="text-slate-400 text-[11px] print:text-[9.5px]">Akwa Ibom State University</div>
          </div>

        </div>

      </div>

      {/* Paystack Acceptance Fee Modal */}
      <PaystackModal
        isOpen={showPaystackModal}
        onClose={() => setShowPaystackModal(false)}
        onSuccess={handleAcceptanceSuccess}
        amount={INSTITUTION_INFO.acceptanceFee}
        purpose="AKSU Business School Acceptance Fee"
        applicantEmail="candidate.office@aksu.edu.ng"
        applicantName={candidate.fullName}
      />

    </div>
  );
};
